import { useMutation } from '@apollo/client';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { UserContext } from '@providers/userContextProvider';
import { Box, Button, Typography, useSnackbar } from 'hephaestus/web';
import {
  type ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { UPDATE_FLAG_NOTES } from '../utils/flags.queries';
import {
  buildUpdateFlagNotesInput,
  getReconFlagNoteAuthorName,
  hasUnsavedFlagNoteDraft,
  type ReconFlagNote,
} from '../utils/reconFlagNotes';
import type { ReconFlagRow } from '../utils/reconFlags.types';

export type ReconFlagsDrawerNotesProps = {
  flag: ReconFlagRow;
  onSaved: (notes: ReconFlagNote[]) => void | Promise<void>;
  /** When true, omits bottom margin and tightens spacing for use in the drawer footer. */
  embeddedInFooter?: boolean;
  /** When set with `onNotesDraftChange`, the field is controlled by the parent (e.g. drawer Resolve). */
  notesDraft?: string;
  onNotesDraftChange?: (next: string) => void;
  /** Hides the inline Save button. */
  hideSaveButton?: boolean;
  /** Hides the section heading (e.g. when the Notes tab label is sufficient). */
  hideHeading?: boolean;
  textFieldDisabled?: boolean;
};

export const ReconFlagsDrawerNotes = ({
  flag,
  onSaved,
  notesDraft: controlledDraft,
  onNotesDraftChange,
  hideSaveButton = false,
  hideHeading = false,
  textFieldDisabled = false,
  embeddedInFooter = false,
}: ReconFlagsDrawerNotesProps) => {
  const { t } = useTranslation(['reconciliation', 'shared']);
  const { enqueueSnackbar } = useSnackbar();
  const { userContext } = useContext(UserContext);
  const noteAuthorName = getReconFlagNoteAuthorName(userContext);
  const [internalDraft, setInternalDraft] = useState('');
  const [updateNotes, { loading }] = useMutation(UPDATE_FLAG_NOTES);

  const isControlled =
    controlledDraft !== undefined && onNotesDraftChange !== undefined;
  const draft = isControlled ? controlledDraft : internalDraft;
  const setDraft = isControlled ? onNotesDraftChange : setInternalDraft;

  useEffect(() => {
    if (!isControlled) {
      setInternalDraft('');
    }
  }, [flag.id, isControlled]);

  const onSave = useCallback(async (): Promise<void> => {
    const trimmed = draft.trim();
    if (!hasUnsavedFlagNoteDraft(trimmed)) {
      return;
    }
    try {
      const { data, errors } = await updateNotes({
        variables: {
          input: buildUpdateFlagNotesInput(flag.id, trimmed, noteAuthorName),
        },
      });
      if (errors?.length || !data?.updateFlagNotes?.flag?.id) {
        enqueueSnackbar(t('reconFlags.drawer.notesSaveError'), {
          variant: 'error',
        });
        return;
      }
      enqueueSnackbar(t('reconFlags.drawer.notesSaved'), {
        variant: 'success',
      });
      const savedNotes = data.updateFlagNotes.flag.notes ?? [];
      if (!isControlled) {
        setInternalDraft('');
      }
      await onSaved(savedNotes);
    } catch {
      enqueueSnackbar(t('reconFlags.drawer.notesSaveError'), {
        variant: 'error',
      });
    }
  }, [
    draft,
    flag.id,
    isControlled,
    noteAuthorName,
    onSaved,
    enqueueSnackbar,
    t,
    updateNotes,
  ]);

  const onCancel = useCallback(() => {
    setDraft('');
  }, [setDraft]);

  const showSave = !hideSaveButton;

  const minRows = embeddedInFooter ? 3 : 4;

  return (
    <Box sx={{ mb: embeddedInFooter ? 0 : 2 }}>
      {!hideHeading && (
        <Typography
          variant='titleMedium'
          sx={{ mb: embeddedInFooter ? 0.75 : 1 }}
        >
          {t('reconFlags.drawer.internalNotesHeading')}
        </Typography>
      )}
      <TextField
        fullWidth
        multiline
        minRows={minRows}
        value={draft}
        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          setDraft(e.target.value)
        }
        placeholder={t('reconFlags.drawer.notesPlaceholder')}
        disabled={textFieldDisabled || loading}
      />
      {showSave && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            mt: 1,
          }}
        >
          <Button
            variant='outlined'
            color='primary'
            size='small'
            disabled={
              loading || textFieldDisabled || !hasUnsavedFlagNoteDraft(draft)
            }
            onClick={onCancel}
          >
            {t('shared:cancel')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            size='small'
            loading={loading}
            disabled={
              loading || textFieldDisabled || !hasUnsavedFlagNoteDraft(draft)
            }
            onClick={() => void onSave()}
          >
            {t('reconFlags.drawer.addNote')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
